<script>
import countlyVue from '../../compat/vue-core';
import i18n from '../../../../i18n';
import { SearchableOptionsMixin, TabbedOptionsMixin } from './commons';

export default countlyVue.components.BaseComponent.extend({
  name: 'ClySelectX',
  mixins: [TabbedOptionsMixin, SearchableOptionsMixin],
  props: {
    title: { type: String, default: '' },
    placeholder: { type: String, default: 'Select' },
    noMatchFoundPlaceholder: { default: i18n.tc('common.search.no-match-found'), required: false },
    value: { type: [String, Number, Array] },
    mode: { type: String, default: 'single-list' }, // multi-check,
    autoCommit: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    width: { type: [Number, Object], default: 400 },
    size: { type: String, default: '' },
    adaptiveLength: { type: Boolean, default: false },
    minInputWidth: {
      type: Number,
      default: -1,
      required: false,
    },
    maxInputWidth: {
      type: Number,
      default: -1,
      required: false,
    },
    showSelectedCount: { type: Boolean, default: false },
    arrow: { type: Boolean, default: true },
    singleOptionSettings: {
      type: Object,
      default() {
        return {
          hideList: false,
          autoPick: false,
        };
      },
      required: false,
    },
    popClass: {
      type: String,
      required: false,
    },
    minItems: {
      type: Number,
      default: 0,
      required: false,
    },
    maxItems: {
      type: Number,
      default: Number.MAX_SAFE_INTEGER,
      required: false,
    },
    hasRemovableOptions: {
      type: Boolean,
      default: false,
      required: false,
    },
    //
    collapseTags: {
      type: Boolean,
      default: true,
      required: false,
    },
    //
    remote: { type: Boolean, default: false },
    remoteMethod: { type: Function, required: false },
  },
  data() {
    return {
      uncommittedValue: null,
    };
  },
  computed: {
    isMultiple() {
      return (`${this.mode}`).startsWith('multi');
    },
    popClasses() {
      return {
        'cly-vue-select-x__pop--hidden-tabs': this.hideDefaultTabs || !this.showTabs,
        'cly-vue-select-x__pop--has-single-option': this.hasSingleOption,
        'cly-vue-select-x__pop--has-slim-header': !this.searchable && !this.showTabs,
      };
    },
    currentTab() {
      const filtered = this.publicTabs.filter((tab) => this.activeTabId === tab.name);
      if (filtered.length > 0) {
        return filtered[0];
      }
      return {};
    },
    hasSingleOption() {
      return (this.activeTabId !== '__root'
                        && this.currentTab.options
                        && this.currentTab.options.length === 1
                        && this.singleOptionSettings.hideList);
    },
    showList() {
      return !this.hasSingleOption;
    },
    innerValue: {
      get() {
        if (this.uncommittedValue && this.uncommittedValue !== this.value) {
          return this.uncommittedValue;
        }
        return this.value;
      },
      set(newVal) {
        if (this.autoCommit && this.isItemCountValid) {
          this.commitValue(newVal);
        } else {
          this.uncommittedValue = newVal;
        }
      },
    },
    selectedCountText() {
      if (this.uncommittedValue) {
        return i18n.tc('export.export-columns-selected-count', this.uncommittedValue.length, (this.options ? this.options.length : 0));
      }

      return i18n.tc('export.export-columns-selected-count', (this.value ? this.value.length : 0), (this.options ? this.options.length : 0));
    },
    isItemCountValid() {
      if (this.mode === 'single-list' || this.autoCommit) {
        return true;
      }
      return Array.isArray(this.innerValue) && this.innerValue.length >= this.minItems && this.innerValue.length <= this.maxItems;
    },
  },
  mounted() {
    this.determineActiveTabId();
  },
  methods: {
    handleValueChange() {
      if (this.mode === 'single-list' && this.autoCommit) {
        this.doClose();
      }
    },
    doClose() {
      this.determineActiveTabId();
      this.$refs.dropdown.handleClose();
    },
    updateDropdown() {
      this.$refs.dropdown.updateDropdown();
    },
    handleDropdownShow() {
      this.$forceUpdate();
      this.focusOnSearch();
    },
    focusOnSearch() {
      this.$nextTick(() => {
        if (this.$refs.searchBox) {
          this.$refs.searchBox.focus();
        }
      });
    },
    focusOnTrigger() {
      if (this.$refs.trigger && this.$refs.trigger.focus()) {
        this.$nextTick(() => {
          this.$refs.trigger.focus();
        });
      }
    },
    doCommit() {
      if (!this.isItemCountValid) {
        return;
      }
      if (this.uncommittedValue) {
        this.commitValue(this.uncommittedValue);
        this.uncommittedValue = null;
      }
      this.doClose();
    },
    doDiscard() {
      this.uncommittedValue = null;
      this.doClose();
    },
    commitValue(val) {
      this.$emit('input', val);
      this.$emit('change', val);
    },
    removeOption(options) {
      this.$emit('remove-option', options);
    },
  },
  watch: {
    searchQuery() {
      this.updateDropdown();
    },
    activeTabId() {
      this.updateDropdown();
      if (this.hasSingleOption && this.singleOptionSettings.autoPick) {
        this.innerValue = this.currentTab.options[0].value;
        this.doCommit();
      }
    },
    value(newVal) {
      if (!this.onlySelectedOptionsTab && this.isMultiple && this.remote && newVal && newVal.length === 0 && this.activeTabId === '__selected') {
        this.navigateToFirstRegularTab();
      }
      this.uncommittedValue = null;
    },
  },
});
</script>
<template>
  <cly-dropdown
    ref="dropdown"
    class="cly-vue-select-x"
    :width="width"
    :placeholder="placeholder"
    :disabled="disabled"
    :pop-class="popClass"
    v-bind="$attrs"
    v-on="$listeners"
    @show="handleDropdownShow"
    @hide="focusOnTrigger"
  >
    <template #trigger="dropdown">
      <slot
        name="trigger"
        :dropdown="dropdown"
      >
        <el-select-head
          v-if="isMultiple"
          ref="trigger"
          multiple
          class="el-select-head"
          :disabled="disabled"
          :opened="dropdown.visible"
          :placeholder="placeholder"
          :arrow="arrow"
          :value="value"
          :collapse-tags="collapseTags"
          :options-lookup="selectedOptions"
          @change="commitValue"
        />
        <cly-input-dropdown-trigger
          v-else
          ref="trigger"
          :size="size"
          :disabled="disabled"
          :adaptive-length="adaptiveLength"
          :min-width="minInputWidth"
          :max-width="maxInputWidth"
          :focused="dropdown.focused"
          :opened="dropdown.visible"
          :placeholder="placeholder"
          :arrow="arrow"
          :selected-options="selectedOptions"
        >
          <template #suffix="opts">
            <slot
              name="input-suffix"
              :selected-options="opts"
            />
          </template>
        </cly-input-dropdown-trigger>
      </slot>
    </template>
    <div
      class="cly-vue-select-x__pop"
      :class="popClasses"
    >
      <div class="cly-vue-select-x__header">
        <div class="bu-level">
          <div
            v-if="title"
            class="cly-vue-select-x__title"
          >
            {{ title }}
          </div>
          <div
            v-if="showSelectedCount"
            class="bu-level-right text-small color-cool-gray-50"
          >
            {{ selectedCountText }}
          </div>
        </div>
        <div
          v-if="!!$scopedSlots.header"
          class="cly-vue-select-x__header-slot"
        >
          <slot
            name="header"
            :active-tab-id="activeTabId"
            :tabs="publicTabs"
            :update-tab="updateTabFn"
          />
        </div>
        <div class="bu-level">
          <div
            v-if="searchable"
            class="bu-level-item"
          >
            <el-input
              v-show="showList"
              ref="searchBox"
              v-model="searchQuery"
              autocomplete="off"
              :disabled="disabled"
              :placeholder="searchPlaceholder"
              @keydown.native.esc.stop.prevent="doClose"
            >
              <i
                slot="prefix"
                class="el-input__icon el-icon-search"
              />
            </el-input>
          </div>
          <slot name="action" />
        </div>
      </div>
      <el-tabs
        v-model="activeTabId"
        v-loading="isQueryPending"
        @keydown.native.esc.stop.prevent="doClose"
      >
        <el-tab-pane
          v-for="tab in publicTabs"
          :key="tab.name"
          :name="tab.name"
        >
          <span slot="label">
            {{ tab.label }}
          </span>
          <cly-listbox
            v-show="showList"
            v-if="mode === 'single-list'"
            v-model="innerValue"
            :no-match-found-placeholder="noMatchFoundPlaceholder"
            :bordered="false"
            :options="getMatching(tab.options)"
            :has-removable-options="hasRemovableOptions"
            @change="handleValueChange"
            @remove-option="removeOption"
          >
            <template #option-prefix="scope">
              <slot
                name="option-prefix"
                v-bind="scope"
              />
            </template>
            <template #option-suffix="scope">
              <slot
                name="option-suffix"
                v-bind="scope"
              />
            </template>
          </cly-listbox>
          <cly-checklistbox
            v-show="showList"
            v-else-if="mode === 'multi-check'"
            v-model="innerValue"
            :no-match-found-placeholder="noMatchFoundPlaceholder"
            :bordered="false"
            :options="getMatching(tab.options)"
            @change="handleValueChange"
          />
          <cly-checklistbox
            v-show="showList"
            v-else-if="mode === 'multi-check-sortable'"
            v-model="innerValue"
            :no-match-found-placeholder="noMatchFoundPlaceholder"
            :sortable="true"
            :bordered="false"
            :options="getMatching(tab.options)"
            @change="handleValueChange"
          />
        </el-tab-pane>
      </el-tabs>
      <div
        v-if="!autoCommit"
        class="cly-vue-select-x__footer"
      >
        <div class="cly-vue-select-x__commit-section">
          <el-button
            type="secondary"
            size="small"
            @click="doDiscard"
          >
            {{ $t("common.cancel") }}
          </el-button>
          <el-button
            :disabled="disabled || !isItemCountValid"
            type="success"
            size="small"
            @click="doCommit"
          >
            {{ $t("common.confirm") }}
          </el-button>
        </div>
      </div>
    </div>
  </cly-dropdown>
</template>
<style lang="scss">
    @use "../../styles/base/colors" as c;
    @use "../../styles/base/typography-variables" as t;
    @use "../../styles/base/variables" as v;
    @use "sass:map";

    .cly-vue-select-x {
        &__pop {
            .cly-vue-listbox {
                background-color: transparent !important;
            }
            .el-tabs {
                &__active-bar{
                    background-color: #0166d6;
                }
                &__nav-wrap::after {
                    background-color: transparent;
                }
                &__nav-scroll {
                    // padding: 0 14px;
                    margin: 0 14px;
                }
                &__header {
                    margin: 0 0 1px 0;
                    z-index: 2000;
                    &::after {
                        // shadow
                        content: "";
                        position: absolute;
                        width: 100%;
                        height: 12px;
                        background: linear-gradient(180deg, #333c48 0%, rgba(51, 60, 72, 0.0001) 100%);
                        top: 40px;
                        opacity: 0.12;
                    }
                }
                // &__active-bar {
                //     padding: 0 15px;
                //     left: -15px;
                // }
            }
            &--hidden-tabs {
                .el-tabs {
                    &__header {
                        &::after {
                            top: 0px;
                        }
                    }
                    &__nav-scroll {
                        display: none;
                    }
                }
            }
            // .cly-vue-listbox {
            //     padding: v.$select-dropdown-item-default-padding;
            // }
        }
        &__title {
            font-weight: 500;
            font-size: 16px;
            line-height: 24px;
            color: #333c48;
            padding-bottom: 16px;
        }
        &__header-slot {
            padding-bottom: 16px;
        }
        &__header {
            padding: 16px;
            input[type=text] {
                background-color: #F6F6F6;
            }
        }
        &__footer {
            border-top: 1px solid #ececec;
        }
        &__commit-section {
            float: right;
            margin: 16px;
        }

        &__pop--has-single-option, &__pop--has-slim-header {
            .cly-vue-select-x__header-slot {
                padding: 0;
            }
        }

        &__pop--has-single-option {
            .el-tabs__header::after {
                content: none;
            }
        }

        .el-select-head {
            width: 100%;
        }
    }
</style>
